import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listChats,
  listOrders,
  analyzeOrders,
  extractCustomers,
  extractItems,
  updateOrder,
  deleteOrder,
  reevaluateOrder,
  getCustomerOrderProfile,
  getOrderSourceMessages,
  getSettings,
  resolvePrices,
} from '../api/client';
import { useRivhitData, rivhitDisplayName } from '../context/RivhitDataContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import EmptyState from '../components/EmptyState';
import PriceModal, { type PriceModalItem } from '../components/PriceModal';

// ── Types ─────────────────────────────────────────────────────────────

interface OrderItem {
  product_id: string;
  product_name: string;
  quantity: number;
  unit: string;
  price?: number | null;
  price_source?: string | null;
  item_confidence?: number;
  usual_quantity?: number;
}

interface Order {
  id: string;
  chat_id: string;
  chat_ids?: string[];
  customer_id: string;
  customer_name: string;
  customer_confidence?: number;
  suggested_customers?: CustomerOption[];
  items: OrderItem[];
  status: string;
  confidence: number;
  notes?: string;
  source_message_ids: string[];
  analysis_run_id?: string;
  detected_at: string;
  created_at: string;
  updated_at: string;
}

interface Chat {
  id: string;
  name: string;
  chat_type: string;
}

interface CustomerOption {
  id: string;
  name: string;
}

interface SourceMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  chat_name: string;
}

interface CandidateCustomer {
  customer_id: string;
  customer_name: string;
  confidence: number;
  suggested_customers: CustomerOption[];
  source_message_indices: number[];
  source_messages: SourceMessage[];
  reasoning: string;
}

interface ConfirmedCandidate {
  customer_id: string;
  customer_name: string;
  source_message_indices: number[];
}

type PageState =
  | 'idle'
  | 'extracting_customers'
  | 'reviewing_candidates'
  | 'extracting_items';

// ── Constants ─────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  detected: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  fulfilled: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_OPTIONS = ['detected', 'confirmed', 'in_progress', 'fulfilled', 'cancelled'] as const;

const STATUS_FLOW: Record<string, { next: string; label: string; icon: string; color: string }> = {
  detected: { next: 'confirmed', label: 'orders.confirm', icon: '✓', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
  confirmed: { next: 'in_progress', label: 'orders.start', icon: '▶', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
  in_progress: { next: 'fulfilled', label: 'orders.fulfill', icon: '✅', color: 'bg-green-50 text-green-700 hover:bg-green-100' },
};

// ── Helpers ────────────────────────────────────────────────────────────

function confidenceDot(c: number): string {
  if (c >= 0.8) return 'bg-green-500';
  if (c >= 0.5) return 'bg-yellow-500';
  return 'bg-red-500';
}

function confidenceBadgeClasses(c: number): string {
  if (c >= 0.8) return 'bg-green-100 text-green-700';
  if (c >= 0.5) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

function relativeTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const mins = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatTimestamp(ts: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) {
    const num = Number(ts);
    if (!isNaN(num)) {
      const date = new Date(num > 1e12 ? num : num * 1000);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
    }
    return '';
  }
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ── Sender colour hash (WhatsApp style) ───────────────────────────────

const WA_SENDER_COLORS = [
  'text-[#1f7aeb]',
  'text-[#e06c75]',
  'text-[#d4a017]',
  'text-[#6b8e23]',
  'text-[#e67e22]',
  'text-[#8e44ad]',
  'text-[#16a085]',
  'text-[#c0392b]',
];

function waSenderColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return WA_SENDER_COLORS[Math.abs(h) % WA_SENDER_COLORS.length];
}

// ── Source Messages Panel ─────────────────────────────────────────────

function SourceMessagesPanel({
  messages,
  loading: isLoading,
}: {
  messages: SourceMessage[];
  loading?: boolean;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 px-2 text-xs text-gray-400">
        <Spinner className="h-3 w-3" />
        {t('orders.loadingMessages', 'Loading source messages...')}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="py-3 px-2 text-xs text-gray-400 italic">
        {t('orders.noSourceMessages', 'No source messages available')}
      </div>
    );
  }

  // Sort messages by timestamp ascending before grouping
  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  // Group messages by chat_name to display per-chat sections
  const chatGroups: { chatName: string; msgs: SourceMessage[] }[] = [];
  for (const msg of sortedMessages) {
    const name = msg.chat_name || t('orders.unknownChat', 'Unknown chat');
    const existing = chatGroups.find((g) => g.chatName === name);
    if (existing) {
      existing.msgs.push(msg);
    } else {
      chatGroups.push({ chatName: name, msgs: [msg] });
    }
  }

  return (
    <div className="rounded-xl overflow-hidden border border-[#d1d7db]">
      {chatGroups.map((group, gi) => (
        <div key={gi}>
          {/* Chat group header – WhatsApp-style teal bar */}
          <div className="bg-[#008069] px-3 py-1.5 flex items-center gap-2">
            <span className="text-white text-xs">👥</span>
            <span className="text-white text-xs font-medium truncate">{group.chatName}</span>
            <span className="text-white/60 text-[10px] ltr:ml-auto rtl:mr-auto">{group.msgs.length} msg</span>
          </div>

          {/* Messages area */}
          <div
            className="space-y-1 max-h-72 overflow-y-auto p-2.5"
            style={{
              backgroundColor: '#e5ddd5',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23c8c8c8\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          >
            {group.msgs.map((msg, i) => {
              const senderColor = waSenderColor(msg.sender);
              return (
                <div key={msg.id || `${gi}-${i}`} className="flex justify-start">
                  <div className="relative max-w-[85%] rounded-lg bg-white shadow-sm px-2.5 py-1.5"
                    style={{ borderTopLeftRadius: '2px' }}
                  >
                    {/* Bubble tail */}
                    <div
                      className="absolute top-0 -left-1.5 w-3 h-3 bg-white"
                      style={{
                        clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                      }}
                    />
                    {/* Sender name */}
                    <p className={`text-[11px] font-semibold mb-0.5 ${senderColor}`}>
                      {msg.sender}
                    </p>
                    {/* Message body */}
                    <p className="text-[13px] text-gray-900 leading-snug whitespace-pre-wrap break-words">
                      {msg.content}
                    </p>
                    {/* Timestamp */}
                    <div className="flex justify-end -mb-0.5 mt-0.5">
                      <span className="text-[10px] text-gray-400">{formatTimestamp(msg.timestamp)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Multi-Chat Selector ───────────────────────────────────────────────

function MultiChatSelector({
  chats,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
}: {
  chats: Chat[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => chats.filter((c) => c.chat_type === 'group'), [chats]);
  const contacts = useMemo(() => chats.filter((c) => c.chat_type !== 'group'), [chats]);

  const lq = query.toLowerCase();
  const filteredGroups = useMemo(
    () => (lq ? groups.filter((c) => c.name.toLowerCase().includes(lq)) : groups),
    [groups, lq],
  );
  const filteredContacts = useMemo(
    () => (lq ? contacts.filter((c) => c.name.toLowerCase().includes(lq)) : contacts),
    [contacts, lq],
  );

  const renderSection = (title: string, icon: string, items: Chat[]) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-2 pb-1">
          {icon} {title}
        </div>
        {items.map((chat) => (
          <label
            key={chat.id}
            className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedIds.has(chat.id)}
              onChange={() => onToggle(chat.id)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
            />
            <span className="text-sm text-gray-700 truncate">{chat.name}</span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Search + actions header */}
      <div className="px-3 py-2 border-b border-gray-100 space-y-2">
        <div className="relative">
          <span className="absolute inset-y-0 ltr:left-2 rtl:right-2 flex items-center text-gray-400 pointer-events-none text-sm">
            🔍
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('orders.searchChats', 'Search chats...')}
            className="w-full rounded border border-gray-200 bg-gray-50 py-1.5 ltr:pl-7 rtl:pr-7 ltr:pr-3 rtl:pl-3 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-400">
            {selectedIds.size > 0
              ? t('orders.selectedCount', '{{count}} selected', { count: selectedIds.size })
              : t('orders.noneSelected', 'None selected')}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onSelectAll}
              className="text-[10px] text-primary-600 hover:text-primary-800 font-medium"
            >
              {t('orders.selectAll', 'Select All')}
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={onClear}
              className="text-[10px] text-gray-500 hover:text-gray-700 font-medium"
            >
              {t('orders.clearSelection', 'Clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Chat list */}
      <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-50">
        {renderSection(t('orders.groups', 'Groups'), '👥', filteredGroups)}
        {renderSection(t('orders.contacts', 'Contacts'), '👤', filteredContacts)}
        {filteredGroups.length === 0 && filteredContacts.length === 0 && (
          <div className="py-4 text-center text-xs text-gray-400">
            {t('common.noResults', 'No results found')}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Customer Combobox Dropdown (infinite scroll) ─────────────────────

function CustomerComboboxDropdown({
  options,
  currentId,
  initialOptions,
  onSelect,
  t,
}: {
  options: CustomerOption[];
  currentId: string;
  initialOptions?: CustomerOption[];
  onSelect: (c: CustomerOption) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}) {
  const { visible, hasMore, sentinelRef } = useInfiniteScroll(options, 50);
  const hasSuggestions = initialOptions && initialOptions.length > 0 && options.some((o) => initialOptions.some((s) => s.id === o.id));

  return (
    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
      {hasSuggestions && (
        <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100 sticky top-0">
          {t('orders.suggestedMatches', '✨ Suggested matches')}
        </div>
      )}
      {visible.map((c) => {
        const isSuggested = initialOptions?.some((s) => s.id === c.id);
        return (
          <button
            key={c.id}
            onClick={() => onSelect(c)}
            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50 transition-colors ${
              c.id === currentId ? 'bg-primary-50 font-medium text-primary-700' : 'text-gray-700'
            }`}
          >
            {c.name}
            <span className="text-[10px] text-gray-400 ltr:ml-2 rtl:mr-2 font-mono">{c.id}</span>
            {isSuggested && <span className="text-[10px] text-amber-500 ltr:ml-1 rtl:mr-1">★</span>}
          </button>
        );
      })}
      {hasMore && (
        <div ref={sentinelRef} className="py-1 text-center text-[10px] text-gray-400">
          {visible.length} / {options.length}
        </div>
      )}
    </div>
  );
}

// ── Customer Combobox ─────────────────────────────────────────────────

function CustomerCombobox({
  currentName,
  currentId,
  confidence,
  onSelect,
  initialOptions,
}: {
  currentName: string;
  currentId: string;
  confidence: number;
  onSelect: (customer: CustomerOption) => void;
  initialOptions?: CustomerOption[];
}) {
  const { t } = useTranslation();
  const {
    configured,
    customers: rivhitCustomers,
    searchCustomers: searchRivhitCustomers,
    ensureCustomersLoaded,
  } = useRivhitData();
  const [query, setQuery] = useState(currentName);
  const [options, setOptions] = useState<CustomerOption[]>(initialOptions ?? []);
  const [open, setOpen] = useState(currentId === 'unknown' && (initialOptions ?? []).length > 0);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Map Rivhit customers to CustomerOption format
  const allCustomerOptions = useMemo(() => {
    return rivhitCustomers.map((c) => ({
      id: String(c.customer_id),
      name: rivhitDisplayName(c),
    }));
  }, [rivhitCustomers]);

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(currentName);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [currentName]);

  // Sync display when currentName changes (e.g., after re-evaluation)
  useEffect(() => {
    if (!open) setQuery(currentName);
  }, [currentName, open]);

  // Build merged options: LLM suggestions first, then Rivhit customers
  const buildOptions = useCallback((customers: CustomerOption[], filterQuery?: string) => {
    const suggestions = initialOptions ?? [];
    const merged: CustomerOption[] = [];
    for (const s of suggestions) {
      if (!filterQuery || s.name.toLowerCase().includes(filterQuery.toLowerCase()) || s.id.toLowerCase().includes(filterQuery.toLowerCase())) {
        merged.push(s);
      }
    }
    for (const c of customers) {
      if (merged.some((m) => m.id === c.id)) continue;
      if (!filterQuery || c.name.toLowerCase().includes(filterQuery.toLowerCase()) || c.id.toLowerCase().includes(filterQuery.toLowerCase())) {
        merged.push(c);
      }
    }
    return merged;
  }, [initialOptions]);

  const handleSearch = useCallback((q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setSearching(true);
      try {
        if (q.length < 1) {
          setOptions(buildOptions(allCustomerOptions));
        } else {
          const results = searchRivhitCustomers(q).map((c) => ({
            id: String(c.customer_id),
            name: rivhitDisplayName(c),
          }));
          setOptions(buildOptions(results, q));
        }
      } catch {
        setOptions(initialOptions ?? []);
      } finally {
        setSearching(false);
      }
    }, q.length < 1 ? 0 : 150);
  }, [initialOptions, allCustomerOptions, buildOptions, searchRivhitCustomers]);

  const handleFocus = useCallback(() => {
    setOpen(true);
    setOptions(buildOptions(allCustomerOptions, query === currentName ? undefined : query));
  }, [allCustomerOptions, buildOptions, query, currentName]);

  const handleSelect = (c: CustomerOption) => {
    setQuery(c.name);
    setOpen(false);
    onSelect(c);
  };

  const highConfidence = confidence >= 0.7;

  // High-confidence: show as text with edit trigger
  if (highConfidence && !open) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">{currentName}</span>
        <button
          onClick={() => setOpen(true)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title={t('orders.editCustomer', 'Change customer')}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${confidenceBadgeClasses(confidence)}`}>
          {Math.round(confidence * 100)}%
        </span>
      </div>
    );
  }

  // Low-confidence or editing: show combobox
  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              handleSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => handleFocus()}
            placeholder={t('orders.searchCustomer', 'Search customer...')}
            className="w-full rounded border border-gray-300 bg-white py-1.5 px-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          />
          {searching && (
            <span className="absolute inset-y-0 ltr:right-2 rtl:left-2 flex items-center">
              <Spinner className="h-3 w-3 text-gray-400" />
            </span>
          )}
        </div>
        <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium shrink-0 ${confidenceBadgeClasses(confidence)}`}>
          {Math.round(confidence * 100)}%
        </span>
      </div>

      {open && options.length > 0 && (
        <CustomerComboboxDropdown
          options={options}
          currentId={currentId}
          initialOptions={initialOptions}
          onSelect={handleSelect}
          t={t}
        />
      )}
    </div>
  );
}

// ── Candidate Card (Phase 1 Review) ───────────────────────────────────

function CandidateCard({
  candidate,
  index,
  confirmed,
  onConfirm,
}: {
  candidate: CandidateCustomer;
  index: number;
  confirmed: ConfirmedCandidate;
  onConfirm: (index: number, update: ConfirmedCandidate) => void;
}) {
  const { t } = useTranslation();
  const [messagesCollapsed, setMessagesCollapsed] = useState(false);

  const handleCustomerSelect = (c: CustomerOption) => {
    onConfirm(index, {
      ...confirmed,
      customer_id: c.id,
      customer_name: c.name,
    });
  };

  return (
    <div className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/30 shadow-sm">
      {/* Card header */}
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-lg">👤</span>
          <div className="flex-1 min-w-0">
            <CustomerCombobox
              currentName={confirmed.customer_name}
              currentId={confirmed.customer_id}
              confidence={candidate.confidence}
              onSelect={handleCustomerSelect}
              initialOptions={candidate.suggested_customers}
            />
          </div>
        </div>

        {/* Reasoning */}
        {candidate.reasoning && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 bg-white/70 rounded px-2.5 py-2 border border-blue-100 italic">
              💡 {candidate.reasoning}
            </p>
          </div>
        )}

        {/* Source messages toggle */}
        <button
          onClick={() => setMessagesCollapsed(!messagesCollapsed)}
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors mb-2"
        >
          <span className={`transition-transform ${messagesCollapsed ? '' : 'rotate-180'}`}>▾</span>
          {t('orders.sourceMessagesCount', '{{count}} source message(s)', { count: candidate.source_messages.length })}
        </button>

        {/* Source messages — shown by default */}
        {!messagesCollapsed && (
          <SourceMessagesPanel messages={candidate.source_messages} />
        )}
      </div>
    </div>
  );
}

// ── Order Card ────────────────────────────────────────────────────────

function OrderCard({
  order,
  chats,
  onStatusChange,
  onDelete,
  onCustomerChange,
  onQuantityChange,
  onPriceChange,
  initialSourceMessages,
}: {
  order: Order;
  chats: Chat[];
  onStatusChange: (orderId: string, newStatus: string) => Promise<void>;
  onDelete: (orderId: string) => Promise<void>;
  onCustomerChange: (orderId: string, customer: CustomerOption) => Promise<void>;
  onQuantityChange: (orderId: string, itemIdx: number, qty: number) => void;
  onPriceChange: (orderId: string, itemIdx: number, price: number | null) => void;
  initialSourceMessages?: SourceMessage[];
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);
  const [reevalBanner, setReevalBanner] = useState<string | null>(null);
  const [orderProfile, setOrderProfile] = useState<Record<string, number> | null>(null);
  const [sourceMessages, setSourceMessages] = useState<SourceMessage[]>(initialSourceMessages ?? []);
  const [sourceMessagesLoading, setSourceMessagesLoading] = useState(false);
  const sourceMessagesFetched = useRef(!!initialSourceMessages);

  // Fetch customer order history for "usually orders" hints
  useEffect(() => {
    if (!order.customer_id || order.customer_id === 'unknown') return;
    getCustomerOrderProfile(order.customer_id)
      .then((res) => {
        const profile = res.data;
        if (profile?.usual_quantities) {
          setOrderProfile(profile.usual_quantities);
        }
      })
      .catch(() => { /* graceful: no hints */ });
  }, [order.customer_id]);

  // Auto-fetch source messages on mount
  useEffect(() => {
    if (sourceMessagesFetched.current) return;
    sourceMessagesFetched.current = true;
    setSourceMessagesLoading(true);
    getOrderSourceMessages(order.id)
      .then((res) => {
        const msgs = Array.isArray(res.data) ? res.data : res.data?.source_messages ?? [];
        setSourceMessages(msgs);
      })
      .catch(() => { /* graceful */ })
      .finally(() => setSourceMessagesLoading(false));
  }, [order.id]);

  const handleCustomerSelect = async (customer: CustomerOption) => {
    setReevaluating(true);
    setReevalBanner(null);
    try {
      await onCustomerChange(order.id, customer);
      setReevalBanner(t('orders.reevaluated', 'Order re-evaluated for {{name}}', { name: customer.name }));
      setTimeout(() => setReevalBanner(null), 4000);
    } catch {
      setReevalBanner(t('orders.reevalFailed', 'Re-evaluation failed'));
      setTimeout(() => setReevalBanner(null), 4000);
    } finally {
      setReevaluating(false);
    }
  };

  const handlePrint = () => {
    alert(`Invoice printed for ${order.customer_name} — ${order.items.length} items`);
  };

  const customerConfidence = order.customer_confidence ?? order.confidence;
  const flowAction = STATUS_FLOW[order.status];
  const chatNames = useMemo(() => {
    const ids = order.chat_ids ?? (order.chat_id ? [order.chat_id] : []);
    return ids.map((id) => chats.find((c) => c.id === id)?.name || id.slice(0, 8));
  }, [order, chats]);

  return (
    <div className={`bg-white rounded-lg border transition-shadow ${reevaluating ? 'border-primary-300 shadow-md' : 'border-gray-200 shadow-sm hover:shadow-md'}`}>
      {/* Reevaluation loading overlay */}
      {reevaluating && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 border-b border-primary-100 text-xs text-primary-700">
          <Spinner className="h-3 w-3" />
          {t('orders.reevaluating', 'Re-evaluating order...')}
        </div>
      )}

      {/* Re-evaluation result banner */}
      {reevalBanner && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700 font-medium">
          ↻ {reevalBanner}
        </div>
      )}

      {/* Card header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-lg">🛒</span>
          <div className="min-w-0 flex-1">
            {/* Customer section */}
            <div onClick={(e) => e.stopPropagation()}>
              <CustomerCombobox
                currentName={order.customer_name}
                currentId={order.customer_id}
                confidence={customerConfidence}
                onSelect={(c) => handleCustomerSelect(c)}
                initialOptions={order.suggested_customers}
              />
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {order.items.length} {order.items.length === 1 ? t('orders.item', 'item') : t('orders.itemPlural', 'items')}
              {' · '}
              {relativeTime(order.detected_at)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ltr:ml-3 rtl:mr-3">
          {/* Connected chat badges */}
          {chatNames.length > 0 && (
            <div className="hidden sm:flex items-center gap-1">
              {chatNames.slice(0, 2).map((name, i) => (
                <span key={i} className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 max-w-[80px] truncate">
                  {name}
                </span>
              ))}
              {chatNames.length > 2 && (
                <span className="text-[10px] text-gray-400">+{chatNames.length - 2}</span>
              )}
            </div>
          )}

          {/* Status badge */}
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
            {t(`orders.status.${order.status}`, order.status)}
          </span>

          {/* Expand chevron */}
          <span className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>
            ▾
          </span>
        </div>
      </div>

      {/* Source Messages — always visible, WhatsApp style */}
      <div className="px-4 pb-3">
        <SourceMessagesPanel messages={sourceMessages} loading={sourceMessagesLoading} />
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Items table */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {t('orders.items', 'Order Items')}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="pb-1.5 font-medium">{t('orders.product', 'Product')}</th>
                    <th className="pb-1.5 font-medium text-right ltr:pr-2 rtl:pl-2">{t('orders.quantity', 'Qty')}</th>
                    <th className="pb-1.5 font-medium text-right ltr:pr-2 rtl:pl-2">{t('orders.unit', 'Unit')}</th>
                    <th className="pb-1.5 font-medium text-right ltr:pr-2 rtl:pl-2">{t('orders.price', 'Price')}</th>
                    <th className="pb-1.5 font-medium text-right ltr:pr-2 rtl:pl-2">{t('orders.total', 'Total')}</th>
                    <th className="pb-1.5 font-medium text-center">{t('orders.confidence', 'Conf.')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => {
                    const itemConf = item.item_confidence ?? order.confidence;
                    const usualQty = orderProfile?.[item.product_id] ?? item.usual_quantity;
                    const itemTotal = (item.price != null && item.quantity) ? item.price * item.quantity : null;
                    const priceSourceIcon = item.price_source === 'customer_history' ? '🔄' : item.price_source === 'product_default' ? '📦' : item.price_source === 'manual' ? '✏️' : '';
                    const priceSourceLabel = item.price_source === 'customer_history'
                      ? t('orders.priceSourceHistory', "Customer's last price")
                      : item.price_source === 'product_default'
                        ? t('orders.priceSourceCatalog', 'Catalog price')
                        : item.price_source === 'manual'
                          ? t('orders.priceSourceManual', 'Manual entry')
                          : '';
                    return (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="py-2">
                          <span className="text-gray-900">{item.product_name}</span>
                          {item.product_id && item.product_id !== 'unknown' && (
                            <span className="text-[10px] text-gray-400 ltr:ml-1 rtl:mr-1 font-mono">{item.product_id}</span>
                          )}
                        </td>
                        <td className="py-2 text-right ltr:pr-2 rtl:pl-2">
                          <div className="flex flex-col items-end">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={item.quantity}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val >= 0) {
                                  onQuantityChange(order.id, idx, val);
                                }
                              }}
                              className="w-20 text-right rounded border border-gray-200 px-2 py-1 text-sm font-medium focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                            />
                            {usualQty != null && usualQty !== item.quantity && (
                              <span className="text-[10px] text-gray-400 mt-0.5 italic">
                                {t('orders.usuallyOrders', 'Usually orders {{qty}} {{unit}}', { qty: usualQty, unit: item.unit })}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 text-right text-gray-500 ltr:pr-2 rtl:pl-2">{item.unit}</td>
                        <td className="py-2 text-right ltr:pr-2 rtl:pl-2">
                          <div className="flex flex-col items-end">
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={item.price ?? ''}
                              placeholder="—"
                              onChange={(e) => {
                                const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                if (val === null || (!isNaN(val) && val >= 0)) {
                                  onPriceChange(order.id, idx, val);
                                }
                              }}
                              className="w-20 text-right rounded border border-gray-200 px-2 py-1 text-sm font-medium focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                            />
                            {priceSourceIcon && (
                              <span className="text-[10px] text-gray-400 mt-0.5" title={priceSourceLabel}>
                                {priceSourceIcon} {priceSourceLabel}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 text-right ltr:pr-2 rtl:pl-2 font-medium text-gray-700">
                          {itemTotal != null ? `₪${itemTotal.toFixed(2)}` : '—'}
                        </td>
                        <td className="py-2 text-center">
                          <span
                            className={`inline-block h-2.5 w-2.5 rounded-full ${confidenceDot(itemConf)}`}
                            title={`${Math.round(itemConf * 100)}%`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                {t('orders.notes', 'Notes')}
              </h4>
              <p className="text-sm text-gray-600 bg-gray-50 rounded p-2">{order.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
            <span>ID: {order.id.slice(0, 8)}…</span>
            {order.analysis_run_id && <span>Run: {order.analysis_run_id.slice(0, 8)}…</span>}
            <span>{order.source_message_ids.length} {t('orders.sourceMessages', 'source message(s)')}</span>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            {/* Status progression */}
            {flowAction && (
              <button
                onClick={() => onStatusChange(order.id, flowAction.next)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${flowAction.color}`}
              >
                {flowAction.icon} {t(flowAction.label, flowAction.next)}
              </button>
            )}

            {/* Cancel (if not already terminal) */}
            {order.status !== 'cancelled' && order.status !== 'fulfilled' && (
              <button
                onClick={() => onStatusChange(order.id, 'cancelled')}
                className="px-3 py-1.5 rounded text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                ✕ {t('orders.status.cancelled', 'Cancelled')}
              </button>
            )}

            {/* Spacer for right-aligned actions */}
            <div className="flex-1" />

            {/* Print Invoice */}
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 rounded text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-1"
            >
              🖨️ {t('orders.printInvoice', 'Print Invoice')}
            </button>

            {/* Delete */}
            <button
              onClick={() => onDelete(order.id)}
              className="px-3 py-1.5 rounded text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              🗑 {t('common.delete', 'Delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Orders Page ──────────────────────────────────────────────────

export default function Orders() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Chat monitoring check
  const [monitoringEnabled, setMonitoringEnabled] = useState<boolean | null>(null);

  // Chats
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('orders_selectedChatIds');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // Controls
  const [hours, setHours] = useState<number>(24);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Orders
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<{ count: number; runId: string } | null>(null);

  // Two-phase flow state
  const [pageState, setPageState] = useState<PageState>('idle');
  const [candidates, setCandidates] = useState<CandidateCustomer[]>([]);
  const [confirmedCandidates, setConfirmedCandidates] = useState<ConfirmedCandidate[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [totalMessages, setTotalMessages] = useState<number>(0);
  // Map from newly created order ID → source messages (from extractItems response)
  const [newOrderSourceMessages, setNewOrderSourceMessages] = useState<Record<string, SourceMessage[]>>({});

  // Price modal state
  const [priceModalOrder, setPriceModalOrder] = useState<Order | null>(null);
  const [priceModalItems, setPriceModalItems] = useState<PriceModalItem[]>([]);

  // Resolve prices for an order's items
  const resolveOrderPrices = useCallback(async (order: Order, force = false) => {
    if (!order.customer_id || order.customer_id === 'unknown' || order.items.length === 0) return;
    try {
      const resp = await resolvePrices({
        customer_id: order.customer_id,
        items: order.items.map((it) => ({ product_id: it.product_id, product_name: it.product_name })),
      });
      const resolved = resp.data.resolved;
      const resolvedMap = new Map(resolved.map((r) => [r.product_id, r]));

      const updatedItems = order.items.map((item) => {
        const r = resolvedMap.get(item.product_id);
        if (!r) return item;
        if (!force && item.price != null) return item; // already has price, don't overwrite unless forced
        return { ...item, price: r.price ?? undefined, price_source: r.price_source ?? undefined };
      });

      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, items: updatedItems } : o)),
      );

      // Check if any items need manual input
      const needsManual = resolved.filter((r) => r.needs_manual_input);
      if (needsManual.length > 0) {
        setPriceModalOrder(order);
        setPriceModalItems(
          needsManual.map((r) => ({
            product_id: r.product_id,
            product_name: r.product_name ?? '',
            price: r.price ?? null,
            price_source: r.price_source ?? null,
            last_purchased_at: r.last_purchased_at ?? null,
            catalog_price: r.catalog_price ?? null,
          })),
        );
      }

      // Persist resolved prices
      updateOrder(order.id, { items: updatedItems }).catch(() => {});
    } catch {
      // Graceful: prices won't be resolved but user can enter manually
    }
  }, []);

  // Persist selected chats across refreshes
  useEffect(() => {
    localStorage.setItem('orders_selectedChatIds', JSON.stringify(Array.from(selectedChatIds)));
  }, [selectedChatIds]);

  // Check if chat monitoring is enabled, then load chats
  useEffect(() => {
    getSettings()
      .then((res) => {
        const s = res.data?.settings ?? res.data ?? {};
        const enabled = s.chat_monitoring_enabled !== false;
        setMonitoringEnabled(enabled);
        if (!enabled) return;
        return listChats();
      })
      .then((res) => {
        if (!res) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.chats ?? [];
        setChats(data);
      })
      .catch(() => setMonitoringEnabled(true));
  }, []);

  // Toggle chat selection
  const toggleChat = useCallback((chatId: string) => {
    setSelectedChatIds((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return next;
    });
  }, []);

  const selectAllChats = useCallback(() => {
    setSelectedChatIds(new Set(chats.map((c) => c.id)));
  }, [chats]);

  const clearChatSelection = useCallback(() => {
    setSelectedChatIds(new Set());
  }, []);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await listOrders(params as any);
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('orders.loadFailed', 'Failed to load orders'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Phase 1: Extract customers (two-phase LangGraph flow)
  const handleExtractCustomers = async () => {
    const ids = Array.from(selectedChatIds);
    if (ids.length === 0) return;
    setPageState('extracting_customers');
    setError(null);
    setAnalysisResult(null);
    try {
      const payload = ids.length === 1
        ? { chat_id: ids[0], hours }
        : { chat_ids: ids, hours };
      const res = await extractCustomers(payload);
      const data = res.data;
      const candidateList: CandidateCustomer[] = data.candidates ?? [];
      setCandidates(candidateList);
      setThreadId(data.thread_id ?? null);
      setTotalMessages(data.total_messages ?? 0);
      // Initialize confirmed candidates from the raw candidates
      setConfirmedCandidates(
        candidateList.map((c) => ({
          customer_id: c.customer_id,
          customer_name: c.customer_name,
          source_message_indices: c.source_message_indices,
        })),
      );
      setPageState('reviewing_candidates');
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('orders.extractCustomersFailed', 'Failed to extract customers'));
      setPageState('idle');
    }
  };

  // Phase 2: Extract items with confirmed customers
  const handleExtractItems = async () => {
    if (!threadId || confirmedCandidates.length === 0) return;
    setPageState('extracting_items');
    setError(null);
    try {
      const res = await extractItems({
        thread_id: threadId,
        confirmed_customers: confirmedCandidates,
      });
      const data = res.data;
      const newOrders: { order: Order; source_messages: SourceMessage[] }[] = data.orders ?? [];
      // Collect source messages for new orders
      const srcMap: Record<string, SourceMessage[]> = {};
      for (const entry of newOrders) {
        if (entry.order?.id && entry.source_messages) {
          srcMap[entry.order.id] = entry.source_messages;
        }
      }
      setNewOrderSourceMessages((prev) => ({ ...prev, ...srcMap }));
      setAnalysisResult({
        count: newOrders.length,
        runId: threadId,
      });
      // Reset two-phase state
      setCandidates([]);
      setConfirmedCandidates([]);
      setThreadId(null);
      setTotalMessages(0);
      setPageState('idle');
      await fetchOrders();
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('orders.extractItemsFailed', 'Failed to extract order items'));
      setPageState('reviewing_candidates');
    }
  };

  // Cancel candidate review
  const handleCancelReview = () => {
    setCandidates([]);
    setConfirmedCandidates([]);
    setThreadId(null);
    setTotalMessages(0);
    setPageState('idle');
  };

  // Update a confirmed candidate
  const handleConfirmCandidate = (index: number, update: ConfirmedCandidate) => {
    setConfirmedCandidates((prev) => prev.map((c, i) => (i === index ? update : c)));
  };

  // Legacy analyze selected chats (old endpoint)
  const handleAnalyze = async () => {
    const ids = Array.from(selectedChatIds);
    if (ids.length === 0) return;
    setAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    try {
      const payload = ids.length === 1
        ? { chat_id: ids[0], hours }
        : { chat_ids: ids, hours };
      const res = await analyzeOrders(payload);
      const data = res.data;
      setAnalysisResult({ count: data.orders_created ?? data.count ?? 0, runId: data.analysis_run_id ?? '' });
      await fetchOrders();
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('orders.analysisFailed', 'Analysis failed'));
    } finally {
      setAnalyzing(false);
    }
  };

  // Status change
  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      await updateOrder(orderId, { status: newStatus });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
    } catch {
      setError(t('orders.statusFailed', 'Failed to update order status'));
    }
  };

  // Delete
  const handleDelete = async (orderId: string) => {
    try {
      await deleteOrder(orderId);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch {
      setError(t('orders.deleteFailed', 'Failed to delete order'));
    }
  };

  // Customer change + re-evaluate
  const handleCustomerChange = async (orderId: string, customer: CustomerOption) => {
    const res = await reevaluateOrder(orderId, { customer_id: customer.id, customer_name: customer.name });
    const updated = res.data;
    
    let updatedOrder: Order | null = null;
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id === orderId) {
          updatedOrder = {
            ...o,
            customer_id: customer.id,
            customer_name: customer.name,
            customer_confidence: updated.customer_confidence ?? o.customer_confidence,
            items: updated.items ?? o.items,
          };
          return updatedOrder;
        }
        return o;
      }),
    );

    if (updatedOrder) {
      resolveOrderPrices(updatedOrder, true);
    }
  };

  // Inline quantity edit
  const handleQuantityChange = useCallback((orderId: string, itemIdx: number, qty: number) => {
    let updatedOrder: Order | null = null;
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        const newItems = o.items.map((item, i) => (i === itemIdx ? { ...item, quantity: qty } : item));
        updatedOrder = { ...o, items: newItems };
        return updatedOrder;
      }),
    );
    // Persist (fire-and-forget)
    if (updatedOrder) {
      updateOrder(orderId, { items: (updatedOrder as Order).items }).catch(() => {});
      resolveOrderPrices(updatedOrder as Order, true);
    }
  }, [orders, resolveOrderPrices]);

  // Inline price edit
  const handlePriceChange = useCallback((orderId: string, itemIdx: number, price: number | null) => {
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        const items = o.items.map((item, i) =>
          i === itemIdx ? { ...item, price, price_source: 'manual' } : item
        );
        return { ...o, items };
      }),
    );
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      const newItems = order.items.map((item, i) =>
        i === itemIdx ? { ...item, price, price_source: 'manual' } : item
      );
      updateOrder(orderId, { items: newItems }).catch(() => {});
    }
  }, [orders]);

  // Handle price modal save
  const handlePriceModalSave = useCallback((prices: Record<string, number>) => {
    if (!priceModalOrder) return;
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== priceModalOrder.id) return o;
        const items = o.items.map((item) => {
          const p = prices[item.product_id];
          if (p === undefined) return item;
          return { ...item, price: p, price_source: 'manual' };
        });
        return { ...o, items };
      }),
    );
    // Persist
    const order = orders.find((o) => o.id === priceModalOrder.id) ?? priceModalOrder;
    const newItems = order.items.map((item) => {
      const p = prices[item.product_id];
      if (p === undefined) return item;
      return { ...item, price: p, price_source: 'manual' };
    });
    updateOrder(priceModalOrder.id, { items: newItems }).catch(() => {});
    setPriceModalOrder(null);
    setPriceModalItems([]);
  }, [priceModalOrder, orders]);

  // Auto-resolve prices when orders change (new orders detected)
  const resolvedOrderIds = useRef(new Set<string>());
  useEffect(() => {
    for (const order of orders) {
      if (resolvedOrderIds.current.has(order.id)) continue;
      resolvedOrderIds.current.add(order.id);
      resolveOrderPrices(order);
    }
  }, [orders, resolveOrderPrices]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (statusFilter !== 'all') {
      result = result.filter((o) => o.status === statusFilter);
    }
    return result;
  }, [orders, statusFilter]);

  const isExtracting = pageState === 'extracting_customers' || pageState === 'extracting_items';

  if (monitoringEnabled === false) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <span className="text-5xl block">🛒</span>
          <h2 className="text-xl font-semibold text-gray-700">{t('orders.monitoringDisabled', 'Chat Monitoring Disabled')}</h2>
          <p className="text-sm text-gray-500">{t('orders.monitoringDisabledDesc', 'Enable chat monitoring in Settings to analyze orders from WhatsApp messages.')}</p>
          <button
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            ⚙️ {t('orders.goToSettings', 'Go to Settings')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('orders.title', 'Orders from Messages')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('orders.subtitle', 'Analyze WhatsApp messages to extract coffee orders')}</p>
      </div>

      {/* Controls + Chat Selector layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left: Multi-Chat Selector */}
        <div className="lg:col-span-1">
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            {t('orders.selectChats', 'Select Chats')}
          </label>
          <MultiChatSelector
            chats={chats}
            selectedIds={selectedChatIds}
            onToggle={toggleChat}
            onSelectAll={selectAllChats}
            onClear={clearChatSelection}
          />
        </div>

        {/* Right: Controls + Results */}
        <div className="lg:col-span-3 space-y-4">
          {/* Controls panel */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-wrap items-end gap-4">
              {/* Hours */}
              <div className="w-28">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('orders.hours', 'Past hours')}
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={hours}
                  onChange={(e) => setHours(Math.max(1, Math.min(168, parseInt(e.target.value) || 24)))}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              </div>

              {/* Primary: Extract Customers (two-phase) */}
              <button
                onClick={handleExtractCustomers}
                disabled={selectedChatIds.size === 0 || isExtracting || pageState === 'reviewing_candidates'}
                className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {pageState === 'extracting_customers' ? (
                  <>
                    <Spinner />
                    {t('orders.extractingCustomers', 'Extracting Customers...')}
                  </>
                ) : (
                  <>🔍 {t('orders.analyze', 'Analyze Selected Chats')}</>
                )}
              </button>

              {/* Secondary: Legacy analyze (old endpoint) */}
              <button
                onClick={handleAnalyze}
                disabled={selectedChatIds.size === 0 || analyzing || isExtracting || pageState === 'reviewing_candidates'}
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {analyzing ? (
                  <>
                    <Spinner />
                    {t('orders.analyzing', 'Analyzing...')}
                  </>
                ) : (
                  <>⚡ {t('orders.quickAnalyze', 'Quick Analyze')}</>
                )}
              </button>

              {/* Status filter */}
              <div className="w-40">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('common.filter', 'Filter')}
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                >
                  <option value="all">{t('orders.allStatuses', 'All statuses')}</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{t(`orders.status.${s}`, s)}</option>
                  ))}
                </select>
              </div>

              {/* Results count */}
              <div className="flex items-center ltr:ml-auto rtl:mr-auto">
                <span className="text-sm text-gray-500">
                  {filteredOrders.length} {t('orders.ordersFound', 'order(s)')}
                </span>
              </div>
            </div>

            {/* Banners */}
            <div className="space-y-2 mt-3 empty:mt-0">
              {analysisResult && (
                <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm flex items-center justify-between">
                  <span className="font-medium text-green-800">
                    ✅ {t('orders.analysisComplete', 'Analysis complete — {{count}} order(s) created', { count: analysisResult.count })}
                  </span>
                  <button onClick={() => setAnalysisResult(null)} className="text-green-500 hover:text-green-700 text-xs ltr:ml-4 rtl:mr-4">✕</button>
                </div>
              )}

              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm flex items-center justify-between">
                  <span className="text-red-700">❌ {error}</span>
                  <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-xs ltr:ml-4 rtl:mr-4">✕</button>
                </div>
              )}
            </div>
          </div>

          {/* Phase 1: Candidate Review Banner + Cards */}
          {pageState === 'reviewing_candidates' && (
            <div className="space-y-4">
              {/* Review banner */}
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-blue-800">
                    🔎 {t('orders.reviewCustomerMatches', 'Review Customer Matches')}
                  </h3>
                  <p className="text-xs text-blue-600 mt-0.5">
                    {t(
                      'orders.reviewCandidatesDesc',
                      '{{count}} customer candidate(s) found in {{total}} message(s). Confirm or change the matches below.',
                      { count: candidates.length, total: totalMessages },
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleCancelReview}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-700 text-xs font-medium hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleExtractItems}
                    disabled={confirmedCandidates.length === 0}
                    className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    ✓ {t('orders.confirmExtractItems', 'Confirm & Extract Items')}
                  </button>
                </div>
              </div>

              {/* Candidate cards */}
              <div className="space-y-3">
                {candidates.map((candidate, idx) => (
                  <CandidateCard
                    key={`${candidate.customer_id}-${idx}`}
                    candidate={candidate}
                    index={idx}
                    confirmed={confirmedCandidates[idx]}
                    onConfirm={handleConfirmCandidate}
                  />
                ))}
              </div>

              {/* Bottom confirm bar */}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={handleCancelReview}
                  className="px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleExtractItems}
                  disabled={confirmedCandidates.length === 0}
                  className="px-5 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  ✓ {t('orders.confirmExtractItems', 'Confirm & Extract Items')}
                </button>
              </div>
            </div>
          )}

          {/* Extracting items spinner */}
          {pageState === 'extracting_items' && (
            <div className="flex items-center justify-center py-16">
              <Spinner className="h-6 w-6 text-blue-500" />
              <span className="ltr:ml-2 rtl:mr-2 text-sm text-blue-600 font-medium">
                {t('orders.extractingItems', 'Extracting order items...')}
              </span>
            </div>
          )}

          {/* Orders list (shown in idle state) */}
          {pageState === 'idle' && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Spinner className="h-6 w-6 text-gray-400" />
                  <span className="ltr:ml-2 rtl:mr-2 text-sm text-gray-400">{t('common.loading', 'Loading...')}</span>
                </div>
              ) : filteredOrders.length === 0 ? (
                <EmptyState
                  icon="🛒"
                  title={t('orders.title', 'Orders from Messages')}
                  description={t('orders.emptyState', 'No orders detected yet. Select chats and click Analyze to extract orders from messages.')}
                />
              ) : (
                <div className="space-y-3">
                  {filteredOrders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      chats={chats}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                      onCustomerChange={handleCustomerChange}
                      onQuantityChange={handleQuantityChange}
                      onPriceChange={handlePriceChange}
                      initialSourceMessages={newOrderSourceMessages[order.id]}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Price resolution modal */}
      {priceModalOrder && priceModalItems.length > 0 && (
        <PriceModal
          items={priceModalItems}
          customerName={priceModalOrder.customer_name}
          onSave={handlePriceModalSave}
          onClose={() => { setPriceModalOrder(null); setPriceModalItems([]); }}
        />
      )}
    </div>
  );
}
